import { useWindowDimensions } from 'react-native';
import { Platform } from 'react-native';
import { useMemo, useRef, useEffect } from 'react';

export interface ResponsiveBreakpoints {
  isSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
  isXLarge: boolean;
}

export interface ResponsiveScaling {
  scaleFactor: number;
  fontSize: number;
  spacing: number;
  iconSize: number;
  buttonHeight: number;
  cardPadding: number;
}

const getResponsiveStyles = () => {
    const { isMobile, isTablet, isDesktop, isShortScreen, isMediumScreen } = breakpoints;

    // Base scale factors for different screen sizes
    const getScaleFactor = () => {
      if (isMobile && isShortScreen) return 0.75;
      if (isMobile) return 0.85;
      if (isTablet) return 0.95;
      return 1;
    };

    const scaleFactor = getScaleFactor();

    return {
      // Spacing system
      spacing: {
        xs: Math.max(4, 8 * scaleFactor),
        sm: Math.max(8, 12 * scaleFactor),
        md: Math.max(12, 16 * scaleFactor),
        lg: Math.max(16, 24 * scaleFactor),
        xl: Math.max(24, 32 * scaleFactor),
        xxl: Math.max(32, 48 * scaleFactor),
      },

      // Container dimensions
      container: {
        maxWidth: isMobile ? '95%' : isTablet ? '85%' : '70%',
        minWidth: Math.max(320, isMobile ? 300 : 500),
        padding: {
          horizontal: isMobile ? '4%' : isTablet ? '6%' : '8%',
          vertical: isShortScreen ? '2%' : isMediumScreen ? '3%' : '4%'
        }
      },

      // Typography scale
      typography: {
        xs: Math.max(10, 12 * scaleFactor),
        sm: Math.max(12, 14 * scaleFactor),
        base: Math.max(14, 16 * scaleFactor),
        lg: Math.max(16, 18 * scaleFactor),
        xl: Math.max(18, 20 * scaleFactor),
        xxl: Math.max(20, 24 * scaleFactor),
      },

      // Component sizing
      components: {
        cardRadius: isMobile ? 12 : 16,
        buttonHeight: Math.max(44, 48 * scaleFactor), // Minimum 44px for touch targets
        inputHeight: Math.max(40, 44 * scaleFactor),
        iconSize: Math.max(20, 24 * scaleFactor),
      }
    };
  };

export const useResponsiveWeb = () => {
  const { width, height } = useWindowDimensions();

  // Store initial height to prevent re-renders from keyboard-triggered height changes on web
  const initialHeightRef = useRef<number | null>(null);
  const stableHeightRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (Platform.OS === 'web' && initialHeightRef.current === null) {
      initialHeightRef.current = height;
      stableHeightRef.current = height;
    }
  }, []);

  // On web, use stable height (initial) to prevent keyboard-triggered re-renders
  useEffect(() => {
    if (Platform.OS === 'web' && initialHeightRef.current !== null && stableHeightRef.current !== null) {
      const heightDelta = Math.abs(height - stableHeightRef.current);
      // Only update if height change is significant and not keyboard-related
      if (heightDelta > 100 && Math.abs(height - initialHeightRef.current) < 50) {
        // This is likely a keyboard appearing/disappearing - ignore it
        return;
      }
      // If height is close to initial, it might be keyboard dismissing - keep stable height
      if (Math.abs(height - initialHeightRef.current) < 200) {
        stableHeightRef.current = initialHeightRef.current;
      } else {
        // Significant change from initial - likely a real window resize
        stableHeightRef.current = height;
        initialHeightRef.current = height;
      }
    }
  }, [height]);

  // Use stable height on web to prevent keyboard-triggered re-renders
  const stableHeight = Platform.OS === 'web' && stableHeightRef.current !== null 
    ? stableHeightRef.current 
    : height;

  const breakpoints = useMemo<ResponsiveBreakpoints>(() => ({
    isSmall: width < 768,
    isMedium: width >= 768 && width < 1024,
    isLarge: width >= 1024 && width < 1440,
    isXLarge: width >= 1440,
  }), [width]);

  const scaling = useMemo<ResponsiveScaling>(() => {
    if (Platform.OS !== 'web') {
      return {
        scaleFactor: 1,
        fontSize: 1,
        spacing: 1,
        iconSize: 1,
        buttonHeight: 1,
        cardPadding: 1,
      };
    }

    let scaleFactor = 1;
    let fontSize = 1;
    let spacing = 1;
    let iconSize = 1;
    let buttonHeight = 1;
    let cardPadding = 1;

    if (breakpoints.isSmall) {
      // Mobile web - slightly smaller
      scaleFactor = 0.9;
      fontSize = 0.95;
      spacing = 0.8;
      iconSize = 0.9;
      buttonHeight = 0.9;
      cardPadding = 0.8;
    } else if (breakpoints.isMedium) {
      // Tablet web - moderate scaling
      scaleFactor = 0.85;
      fontSize = 0.9;
      spacing = 0.75;
      iconSize = 0.85;
      buttonHeight = 0.85;
      cardPadding = 0.75;
    } else if (breakpoints.isLarge) {
      // Laptop web - more compact
      scaleFactor = 0.8;
      fontSize = 0.85;
      spacing = 0.7;
      iconSize = 0.8;
      buttonHeight = 0.8;
      cardPadding = 0.7;
    } else {
      // Large desktop - keep text readable but compact spacing
      scaleFactor = 0.85;
      fontSize = 0.9;
      spacing = 0.75;
      iconSize = 0.85;
      buttonHeight = 0.85;
      cardPadding = 0.75;
    }

    return {
      scaleFactor,
      fontSize,
      spacing,
      iconSize,
      buttonHeight,
      cardPadding,
    };
  }, [breakpoints]);

  const getResponsiveValue = (baseValue: number, type: keyof ResponsiveScaling = 'scaleFactor') => {
    return Math.max(baseValue * scaling[type], baseValue * 0.5); // Minimum 50% of original size
  };

  const getResponsivePadding = (basePadding: number) => {
    return getResponsiveValue(basePadding, 'spacing');
  };

  const getResponsiveFontSize = (baseFontSize: number) => {
    return Math.max(getResponsiveValue(baseFontSize, 'fontSize'), 12); // Minimum 12px font
  };

  const getResponsiveIconSize = (baseIconSize: number) => {
    return Math.max(getResponsiveValue(baseIconSize, 'iconSize'), 16); // Minimum 16px icons
  };

  const getResponsiveButtonHeight = (baseHeight: number) => {
    return Math.max(getResponsiveValue(baseHeight, 'buttonHeight'), 36); // Minimum 36px button height
  };

  const getResponsiveCardPadding = (basePadding: number) => {
    return Math.max(getResponsiveValue(basePadding, 'cardPadding'), 8); // Minimum 8px card padding
  };

  return {
    width,
    height: stableHeight, // Return stable height on web to prevent keyboard-triggered re-renders
    breakpoints,
    scaling,
    isWeb: Platform.OS === 'web',
    getResponsiveValue,
    getResponsivePadding,
    getResponsiveStyles,
    getResponsiveFontSize,
    getResponsiveIconSize,
    getResponsiveButtonHeight,
    getResponsiveCardPadding,
  };
};

export default useResponsiveWeb;
